import React from "react";
import { Link } from "react-router-dom";
import { Sun, Phone, MessageCircle, CheckCircle2, ArrowLeft, TrendingUp, Droplets, ShieldCheck } from "lucide-react";
import { CONTACTS, SOLAR_IMAGE } from "@/data/content";
import ContactForm from "@/components/ContactForm";

const SOLAR = "#6BC8DD";
const SOLAR_DEEP = "#2091AB";

const FotovoltaicoPage = () => {
  React.useEffect(() => {
    document.title = "Pulizia Pannelli Fotovoltaici SolarClean · Spazzacamino Davide";
  }, []);

  return (
    <div data-testid="fotovoltaico-page">
      {/* HERO */}
      <section
        className="grain"
        style={{ background: "linear-gradient(180deg, var(--bg-primary) 0%, #0F1417 100%)" }}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm mb-6 hover:opacity-70"
              style={{ color: SOLAR }}
              data-testid="back-home-fv"
            >
              <ArrowLeft size={16} /> Torna alla home
            </Link>
            <span
              className="eyebrow"
              style={{
                color: SOLAR,
                borderColor: "rgba(107, 200, 221, 0.3)",
                backgroundColor: "rgba(107, 200, 221, 0.06)",
              }}
            >
              <Sun size={14} className="inline mr-1.5 -mt-0.5" /> SolarClean · Ditta di Manuel
            </span>
            <h1 className="mt-6 text-4xl md:text-6xl">
              Pulizia <em style={{ color: SOLAR }}>pannelli fotovoltaici</em>.
            </h1>
            <p className="mt-6 text-lg" style={{ color: "var(--text-secondary)" }}>
              In collaborazione con <strong style={{ color: "var(--text-primary)" }}>SolarClean</strong>,
              la ditta specializzata di Manuel, offriamo un servizio professionale di pulizia dei
              pannelli fotovoltaici in tutta l'Emilia.
            </p>
            <p className="mt-4 text-lg" style={{ color: "var(--text-secondary)" }}>
              Lo sporco, la polvere e i depositi organici possono ridurre il rendimento
              dei pannelli fino al <strong style={{ color: "var(--text-primary)" }}>30%</strong>.
              Una pulizia annuale professionale ti fa recuperare energia e denaro.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={`tel:${CONTACTS.phoneRaw}`}
                className="btn-primary"
                style={{
                  background: `linear-gradient(180deg, ${SOLAR_DEEP} 0%, #0F6E82 100%)`,
                  boxShadow: `0 6px 20px rgba(32, 145, 171, 0.35)`,
                }}
                data-testid="fv-call-btn"
              >
                <Phone size={18} /> Chiama {CONTACTS.phoneDisplay}
              </a>
              <a
                href={`https://wa.me/${CONTACTS.whatsapp}?text=${encodeURIComponent("Ciao, vorrei un preventivo SolarClean per la pulizia dei pannelli fotovoltaici.")}`}
                target="_blank" rel="noreferrer"
                className="btn-whatsapp"
                data-testid="fv-whatsapp-btn"
              >
                <MessageCircle size={18} /> WhatsApp
              </a>
            </div>
          </div>
          <div>
            <img
              src={SOLAR_IMAGE}
              alt="Pulizia pannelli fotovoltaici SolarClean"
              className="rounded-2xl w-full h-[420px] md:h-[520px] object-cover"
              style={{
                border: "1px solid rgba(107, 200, 221, 0.3)",
                boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
              }}
              data-testid="fv-hero-image"
            />
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl mb-12">
            <h2 className="text-3xl md:text-5xl">Perché pulire i pannelli?</h2>
            <p className="mt-5 text-lg" style={{ color: "var(--text-secondary)" }}>
              Un impianto pulito è un impianto che rende. Ecco i vantaggi di una pulizia professionale.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            <Benefit
              icon={<TrendingUp size={22} />}
              title="Fino al +30% rendimento"
              desc="Recupera l'energia persa a causa di sporco, polvere e depositi organici."
            />
            <Benefit
              icon={<Droplets size={22} />}
              title="Acqua demineralizzata"
              desc="Nessun agente chimico aggressivo, solo acqua pura per non danneggiare le celle."
            />
            <Benefit
              icon={<ShieldCheck size={22} />}
              title="Sicurezza garantita"
              desc="Attrezzatura professionale, DPI e personale abilitato ai lavori in quota."
            />
          </div>
        </div>
      </section>

      {/* WHY SOLARCLEAN */}
      <section className="py-20 grain" style={{ backgroundColor: "var(--bg-secondary)" }}>
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <span
                className="eyebrow"
                style={{
                  color: SOLAR,
                  borderColor: "rgba(107, 200, 221, 0.3)",
                  backgroundColor: "rgba(107, 200, 221, 0.06)",
                }}
              >
                SolarClean
              </span>
              <h2 className="mt-5 text-3xl md:text-5xl">
                La ditta di <em style={{ color: SOLAR }}>Manuel</em>, figlio d'arte.
              </h2>
              <p className="mt-6 text-lg" style={{ color: "var(--text-secondary)" }}>
                Dopo essere entrato nell'attività di famiglia come spazzacamino nel 2022,
                Manuel ha fondato SolarClean per portare la stessa cura artigianale
                nella pulizia dei pannelli fotovoltaici.
              </p>
            </div>
            <div>
              <h3 className="text-2xl mb-4">Cosa include la pulizia</h3>
              <ul className="space-y-3">
                <FeatureLi>Sopralluogo e preventivo gratuito</FeatureLi>
                <FeatureLi>Pulizia con acqua demineralizzata e attrezzatura professionale</FeatureLi>
                <FeatureLi>Rimozione muschio, guano, polline, polveri</FeatureLi>
                <FeatureLi>Verifica visiva dello stato dei pannelli</FeatureLi>
                <FeatureLi>Documentazione fotografica prima/dopo</FeatureLi>
                <FeatureLi>Consigli sulla pulizia periodica successiva</FeatureLi>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-5 md:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-5xl">Richiedi un preventivo SolarClean</h2>
            <p className="mt-5 text-lg" style={{ color: "var(--text-secondary)" }}>
              Compila il modulo o contattaci direttamente per un intervento su misura.
            </p>
          </div>
          <div className="card-warm p-6 md:p-8">
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
};

const Benefit = ({ icon, title, desc }) => (
  <div className="card-warm p-7">
    <div
      className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
      style={{
        background: "linear-gradient(180deg, rgba(107, 200, 221, 0.15) 0%, rgba(32, 145, 171, 0.08) 100%)",
        border: "1px solid rgba(107, 200, 221, 0.3)",
        color: SOLAR,
      }}
    >
      {icon}
    </div>
    <h3 className="text-xl mb-2">{title}</h3>
    <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{desc}</p>
  </div>
);

const FeatureLi = ({ children }) => (
  <li className="flex items-start gap-2">
    <CheckCircle2 size={20} className="mt-0.5 flex-shrink-0" style={{ color: SOLAR }} />
    <span style={{ color: "var(--text-primary)" }}>{children}</span>
  </li>
);

export default FotovoltaicoPage;
